import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LanguageTranslator from '../../locals/LanguageTranslator'
import TextWithTooltip from '../../genericComponents/TextWithTooltip'
import TaskManagerListItem from './TaskManagerListItem'
import TaskManagerDetailPanel from './TaskManagerDetailPanel'
import TaskPreviewModal from './TaskPreviewModal'
import TeamTasksDropdown from './TeamTasksDropdown'
import CreateTaskModal from './CreateTaskModal'
import ParentModal from '../../genericComponents/ParentModal'
import NoDataFound from '../../genericComponents/NoDataFound'
import GenericSpinnerLoader from '../../loaders/GenericSpinnerLoader'
import Comments from '../digiVend/Comments/Comments'
import { TaskMentionsContext, CommentContext } from '../AppStack'
import { dispatchHandler } from '../../helper/apiHelperFunctions'
import { parseJwt } from '../../helper/genericFunction'
import { getPayloadFromURLParams } from '../../helper/URLParamsHelper'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { DollarSign, ClipboardCheck, FileText, CheckCircle, AtSign, ClipboardList, Plus, ChevronDown, Check } from 'lucide-react'

const TASK_TYPE_META = {
    APPROVAL: { label: 'Approvals', icon: DollarSign },
    REVIEW: { label: 'Reviews', icon: ClipboardCheck },
    DOCUMENT_SUBMISSION: { label: 'Document Submissions', icon: FileText },
    VERIFICATION: { label: 'Verifications', icon: CheckCircle },
    ACKNOWLEDGEMENT: { label: 'Acknowledgements', icon: AtSign },
}

const humanizeTaskType = (taskType) =>
    taskType ? taskType.toLowerCase().split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '..'

const getTaskTypeMeta = (taskType) => TASK_TYPE_META[taskType] || { label: humanizeTaskType(taskType), icon: ClipboardList }

const TABS = {
    takeActions: 'Take Actions',
    comments: 'Comments',
    // notifications: 'Notifications',
    archieve: 'Archieve',
}

// const STATIC_TAB_COUNTS = { takeActions: 20, notifications: 22, comments: 22, archieve: 22 }

const SORT_OPTIONS = ['Newest', 'Oldest']
const PAGE_SIZE = 20

const TaskManagerPage = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const assignee = parseJwt(sessionStorage.getItem('token'))?.jti
    const { taskCountWithUnreadMentions, mentions } = useContext(TaskMentionsContext)
    const { unreadComments } = useContext(CommentContext)

    const teamTaskCountsData = useSelector((state) => state?.taskManagerStore?.getTeamTaskCountsData)
    const taskTypeCountsData = useSelector((state) => state?.taskManagerStore?.getTaskTypeCountsData)
    const taskListData = useSelector((state) => state?.taskManagerStore?.getTaskListData)
    const taskDetailsData = useSelector((state) => state?.taskManagerStore?.getTaskDetailsData)
    const taskPrioritiesData = useSelector((state) => state?.taskManagerStore?.getTaskPrioritiesData)
    const commentsDocumentsListData = useSelector((state) => state?.generalStore?.getCommentsDocumentsListData)

    const [currentTab, setCurrentTab] = useState('takeActions')
    // Whose tasks the rail/list are showing - the logged-in user ("My Tasks") by default, or a teammate picked from the dropdown
    const [viewAssignee, setViewAssignee] = useState(assignee)
    // "All tasks" picked in the team dropdown - label only until the list API supports it
    const [isAllTasksView, setIsAllTasksView] = useState(false)
    const [activeTaskType, setActiveTaskType] = useState(null)
    const [selectedTaskId, setSelectedTaskId] = useState(null)
    const [priorityFilter, setPriorityFilter] = useState('All')
    const [sortBy, setSortBy] = useState('Newest')
    const [openDropdown, setOpenDropdown] = useState(null)
    const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false)
    const [priorityOverride, setPriorityOverride] = useState({})
    const [taskListItems, setTaskListItems] = useState([])
    const [pinnedTask, setPinnedTask] = useState(null)
    const [isPinnedTaskLeaving, setIsPinnedTaskLeaving] = useState(false)
    const [taskListPage, setTaskListPage] = useState(1)
    const [hasMoreTasks, setHasMoreTasks] = useState(true)
    const [isMentionsView, setIsMentionsView] = useState(false)
    const [mentionsTaskCache, setMentionsTaskCache] = useState({})
    const [mentionsFetchQueue, setMentionsFetchQueue] = useState([])
    const [isSelectingTask, setIsSelectingTask] = useState(false)
    const [pendingScrollCommentId, setPendingScrollCommentId] = useState(null)
    const [isTabSwitching, setIsTabSwitching] = useState(false)
    const mentionsQueueBusyIdRef = useRef(null)
    const listDropdownRef = useRef(null)
    const teamDropdownRef = useRef(null)
    // Whether Take Actions has already auto-switched an empty "My Tasks" view to a teammate - latched so an
    // explicit pick of "My Tasks" afterwards isn't bounced away again; re-armed on returning to Take Actions
    const autoTeamSwitchDoneRef = useRef(false)
    // Which list tab (takeActions/archieve) the rail + list data currently belongs to - Comments sits between them
    // and doesn't touch that data, so this (not currentTab) decides whether arriving on a list tab needs a reload
    const loadedListTabRef = useRef('takeActions')
    const listObserverRef = useRef(null)
    const pinnedTaskRemovalTimeoutRef = useRef(null)
    const pinnedTaskLeaveTimeoutRef = useRef(null)

    useEffect(() => () => {
        clearTimeout(pinnedTaskRemovalTimeoutRef.current)
        clearTimeout(pinnedTaskLeaveTimeoutRef.current)
    }, [])

    // A shared task link (?taskId=...) puts the page into a minimal, single-task "visitor" view:
    // no rail, no list, no other tasks fetched — just that one task's details. But if the link
    // turns out to be to the viewer's own task (they're the current assignee), treat them as the
    // owner instead: fall through to the normal dashboard, with that task selected/pinned there.
    const { taskId: sharedTaskId } = getPayloadFromURLParams({ l: location })
    // Latched, not re-derived every render: getTaskDetailsData is a single shared slice that this
    // page (and Comments.jsx) refetches for lots of unrelated reasons - tab-return, the list's
    // default first row, the mentions queue, explicit selection. Re-checking "does the CURRENT
    // taskDetailsData match sharedTaskId" on every render meant any one of those unrelated fetches
    // (isSuccess resets to false on every _REQUEST, per apiFunction.js) would flip ownership back
    // to false mid-flight and pop the visitor modal open again - a real flicker loop. Once
    // confirmed, ownership must stick for the rest of this page's life regardless of later fetches.
    const [ownedSharedTask, setOwnedSharedTask] = useState(null)
    useEffect(() => {
        if (ownedSharedTask || !sharedTaskId || !taskDetailsData?.isSuccess) return
        const fetchedTask = taskDetailsData?.data?.resource?.task
        if (fetchedTask && String(fetchedTask.id) === String(sharedTaskId) && String(fetchedTask.current_assignee_id) === String(assignee)) {
            setOwnedSharedTask(fetchedTask)
        }
    }, [taskDetailsData?.isSuccess])
    const isSharedTaskView = !!sharedTaskId && !ownedSharedTask

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (![listDropdownRef, teamDropdownRef].some((ref) => ref.current?.contains(event.target))) {
                setOpenDropdown(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Fetch left-rail counts, and warm the (session-cached) priorities list, for the normal (own tasks) view.
    // Skipped for a shared task link, and re-run whenever the user leaves that view (e.g. header nav to a
    // plain /task-manager URL) so their own tasks load instead of leaving the page empty.
    useEffect(() => {
        if (isSharedTaskView) return
        // Own-task shared link: keep it selected instead of clearing it out from under the fetch below.
        if (!sharedTaskId) setSelectedTaskId(null)
        dispatchHandler(dispatch, "getTaskTypeCountsRequest", { assignee: viewAssignee })
        // Team dropdown options - fetched once here on page load, not on every tab/member switch.
        dispatchHandler(dispatch, "getTeamTaskCountsRequest")
        if (!taskPrioritiesData?.isSuccess) {
            dispatchHandler(dispatch, "getTaskPrioritiesRequest")
        }
        // Warms the list backing the "Comments" tab badge - the badge counts unread comments only
        // among these documents, not the user's app-wide unread map (that spans every document type).
        dispatchHandler(dispatch, "getCommentsDocumentsListRequest", { pageNo: 1, pageSize: 30, search: "", filter: {} })
    }, [dispatch, isSharedTaskView])

    // Comments tab dispatches getTaskDetailsRequest for whatever task it has open, overwriting the same
    // shared getTaskDetailsData slice this tab reads for its own selected task. Re-fetch on return so the
    // detail panel doesn't show stale/mismatched data (or go blank) after visiting Comments.
    useEffect(() => {
        if ((currentTab === 'takeActions' || currentTab === 'archieve') && selectedTaskId) {
            dispatchHandler(dispatch, "getTaskDetailsRequest", { id: selectedTaskId })
        }
    }, [currentTab])

    // If the URL carries a shared task id (from a "Share" link), fetch that task's details directly.
    // The details API only checks org membership, not task ownership, so this works across users in the same org.
    useEffect(() => {
        if (sharedTaskId) {
            setSelectedTaskId(sharedTaskId)
            dispatchHandler(dispatch, "getTaskDetailsRequest", { id: sharedTaskId })
        }
    }, [sharedTaskId])

    // Fetches one page of the task list for the given taskType, using the current sort/priority filters unless overridden
    const fetchTaskList = (page, overrides = {}) => {
        const taskType = overrides.taskType ?? activeTaskType
        const sort = overrides.sort ?? sortBy
        const priority = overrides.priority ?? priorityFilter
        const status = overrides.status ?? (currentTab === 'archieve' ? 'COMPLETED' : undefined)
        dispatchHandler(dispatch, "getTaskListRequest", {
            assignee: viewAssignee,
            taskType,
            page,
            limit: PAGE_SIZE,
            sort: sort.toLowerCase(),
            ...(priority !== 'All' ? { priority } : {}),
            ...(status ? { status } : {}),
        })
    }

    // Any change to taskType/sort/priority starts a fresh page 1, discarding previously accumulated pages
    // (taskListItems is left as-is until the new page 1 response replaces it, to avoid an empty-state flash)
    const resetTaskList = (overrides) => {
        setTaskListPage(1)
        setHasMoreTasks(true)
        fetchTaskList(1, overrides)
    }

    // type-counts success -> default to the highest-count category -> fetch its task list
    useEffect(() => {
        if (taskTypeCountsData?.isSuccess) {
            const counts = taskTypeCountsData?.data?.resource || []
            if (!counts.length) {
                // Nothing to list (e.g. a teammate/archive with no tasks) - drop the previous view's rows and release the loader
                setActiveTaskType(null)
                setTaskListItems([])
                // Otherwise the previous tab's (pending) task stays selected and its details keep showing in the panel
                setSelectedTaskId(null)
                setIsTabSwitching(false)
                return
            }
            const topTaskType = [...counts].sort((a, b) => b.count - a.count)[0]?.task_type
            setActiveTaskType(topTaskType)
            resetTaskList({ taskType: topTaskType })
        }
    }, [taskTypeCountsData?.isSuccess])

    // task list success -> append this page's rows, track if more pages exist, and (page 1 only) select the first task
    useEffect(() => {
        if (taskListData?.isSuccess) {
            const rows = taskListData?.data?.resource || []
            setTaskListItems((prev) => (taskListPage === 1 ? rows : [...prev, ...rows]))
            setHasMoreTasks(rows.length === PAGE_SIZE)
            if (taskListPage === 1) {
                setIsTabSwitching(false)
                if (sharedTaskId && ownedSharedTask) {
                    // Already selected/fetched via the shared-link effect - pin it in if this page didn't happen to include it.
                    if (!rows.some((row) => String(row.id) === String(sharedTaskId))) {
                        setPinnedTask(ownedSharedTask)
                    }
                } else {
                    const firstId = rows?.[0]?.id || null
                    setSelectedTaskId(firstId)
                    if (firstId) {
                        dispatchHandler(dispatch, "getTaskDetailsRequest", { id: firstId })
                    }
                }
            }
        }
    }, [taskListData?.isSuccess])

    // Safety net so a failed fetch during a tab switch doesn't leave the full-page loader stuck forever
    useEffect(() => {
        if (taskListData?.isError || taskTypeCountsData?.isError) setIsTabSwitching(false)
    }, [taskListData?.isError, taskTypeCountsData?.isError])

    // Unique, most-recently-mentioned-first list of task ids the user is mentioned in, restricted to
    // tasks still Pending - a mention stays in this list regardless of read state, and only drops off
    // once the underlying task's status moves past Pending (backend sends `status` on every mention).
    const mentionDocumentIds = useMemo(() => {
        const seen = new Set()
        const ids = []
        ;(mentions || []).forEach((m) => {
            if (m?.documentId == null || seen.has(m.documentId)) return
            seen.add(m.documentId)
            if ((m.status || 'PENDING').toUpperCase() === 'PENDING') {
                ids.push(m.documentId)
            }
        })
        return ids
    }, [mentions])

    // Latest mention per task id - drives both the "<name> mentioned you" list row and the scroll target on click
    const latestMentionByTaskId = useMemo(() => {
        const map = {}
        ;(mentions || []).forEach((m) => {
            if (m?.documentId == null) return
            const existing = map[m.documentId]
            if (!existing || (m.creationTime || '') > (existing.creationTime || '')) {
                map[m.documentId] = m
            }
        })
        return map
    }, [mentions])

    // Seed the background fetch queue whenever the mentioned-task set grows with ids we haven't cached yet
    useEffect(() => {
        if (!isMentionsView) return
        const missing = mentionDocumentIds.filter((id) => !mentionsTaskCache[id] && !mentionsFetchQueue.includes(id))
        if (missing.length) {
            setMentionsFetchQueue((prev) => [...prev, ...missing])
        }
    }, [isMentionsView, mentionDocumentIds])

    // Drive the queue one id at a time, pausing while an explicit task selection is in flight
    // to avoid racing with it on the shared getTaskDetailsData slice.
    useEffect(() => {
        if (!isMentionsView) return
        if (isSelectingTask) return
        if (mentionsQueueBusyIdRef.current != null) return
        if (!mentionsFetchQueue.length) return
        const nextId = mentionsFetchQueue[0]
        mentionsQueueBusyIdRef.current = nextId
        dispatchHandler(dispatch, "getTaskDetailsRequest", { id: nextId })
    }, [isMentionsView, mentionsFetchQueue, isSelectingTask])

    // Cache every successful task-details fetch and release whichever lock (queue/selection) it satisfies
    useEffect(() => {
        if (!taskDetailsData?.isSuccess) return
        const fetchedTask = taskDetailsData?.data?.resource?.task
        if (!fetchedTask?.id) return

        if (isSelectingTask && String(fetchedTask.id) === String(selectedTaskId)) {
            setIsSelectingTask(false)
        }

        if (String(fetchedTask.id) === String(mentionsQueueBusyIdRef.current)) {
            mentionsQueueBusyIdRef.current = null
        }

        // The pinned row starts out from the lightweight childTasks[] summary (missing fields
        // like created_by), so swap in the full fetched task as soon as it's available.
        if (pinnedTask && String(fetchedTask.id) === String(pinnedTask.id)) {
            setPinnedTask(fetchedTask)
        }

        // Detail panel re-fetches task details after actions like completing/reassigning a task,
        // but that only updates this shared slice - the list row keeps its stale status/assignee
        // until patched here too.
        setTaskListItems((prev) => prev.map((item) => (
            String(item.id) === String(fetchedTask.id)
                ? { ...item, status: fetchedTask.status, current_assignee_id: fetchedTask.current_assignee_id, assignee_name: fetchedTask.assignee_name }
                : item
        )))

        if (isMentionsView) {
            setMentionsTaskCache((prev) => (prev[fetchedTask.id] ? prev : { ...prev, [fetchedTask.id]: fetchedTask }))
            setMentionsFetchQueue((prev) => prev.filter((id) => String(id) !== String(fetchedTask.id)))
        }
    }, [taskDetailsData?.isSuccess])

    const loadMoreTasks = () => {
        if (!hasMoreTasks || taskListData?.isLoading) return
        const nextPage = taskListPage + 1
        setTaskListPage(nextPage)
        fetchTaskList(nextPage)
    }

    // IntersectionObserver sentinel (same pattern as genericComponents/ListComponent.jsx) instead of a scroll listener
    const listSentinelRef = useCallback((node) => {
        if (taskListData?.isLoading || !hasMoreTasks) return
        if (listObserverRef.current) listObserverRef.current.disconnect()
        listObserverRef.current = new IntersectionObserver((entries) => {
            if (entries?.[0]?.isIntersecting) {
                loadMoreTasks()
            }
        })
        if (node) listObserverRef.current.observe(node)
    }, [taskListData?.isLoading, hasMoreTasks, taskListPage, activeTaskType, sortBy, priorityFilter])

    const handleTaskTypeClick = (taskType) => {
        const isRealSwitch = isMentionsView || taskType !== activeTaskType
        setIsMentionsView(false)
        if (!isRealSwitch) return
        // Leaving Mentions (or switching task types) invalidates whatever was selected in the
        // previous view - clear it immediately so the right panel doesn't keep showing a stale
        // task while the new list is empty or still loading.
        setSelectedTaskId(null)
        setPendingScrollCommentId(null)
        setIsSelectingTask(false)
        clearTimeout(pinnedTaskRemovalTimeoutRef.current)
        clearTimeout(pinnedTaskLeaveTimeoutRef.current)
        setIsPinnedTaskLeaving(false)
        setPinnedTask(null)
        if (taskType !== activeTaskType) {
            setActiveTaskType(taskType)
            resetTaskList({ taskType })
        }
    }

    // Take Actions and Archieve share the same rail/list/detail UI, scoped by status - switching
    // between them re-fetches the rail counts (which cascades into a fresh task list via the
    // effect below). isTabSwitching covers the old content with a full-page loader for that
    // stretch, instead of leaving stale rows/selection visible underneath.
    const reloadTaskTypeCounts = (tab, assigneeId) => {
        setIsMentionsView(false)
        setPendingScrollCommentId(null)
        setIsSelectingTask(false)
        clearTimeout(pinnedTaskRemovalTimeoutRef.current)
        clearTimeout(pinnedTaskLeaveTimeoutRef.current)
        setIsPinnedTaskLeaving(false)
        setPinnedTask(null)
        setIsTabSwitching(true)
        dispatchHandler(dispatch, "getTaskTypeCountsRequest", { assignee: assigneeId, ...(tab === 'archieve' ? { status: 'COMPLETED' } : {}) })
    }

    const handleTabChange = (key) => {
        const isListTab = (tab) => tab === 'takeActions' || tab === 'archieve'
        if (isListTab(key) && key !== loadedListTabRef.current) {
            loadedListTabRef.current = key
            if (key === 'takeActions') autoTeamSwitchDoneRef.current = false
            // Team dropdown is Take Actions-only - Archieve always shows the user's own tasks, so leaving resets back to "My Tasks"
            setViewAssignee(assignee)
            setIsAllTasksView(false)
            reloadTaskTypeCounts(key, assignee)
        }
        setCurrentTab(key)
    }

    // Switching between "My Tasks" and a teammate re-scopes the rail counts (and, via the counts effect, the task list) to that assignee
    const handleTeamMemberChange = (memberId) => {
        setOpenDropdown(null)
        setIsAllTasksView(false)
        if (String(memberId) === String(viewAssignee)) return
        setViewAssignee(memberId)
        setSelectedTaskId(null)
        reloadTaskTypeCounts(currentTab, memberId)
    }

    // TODO: "All tasks" only changes the dropdown label for now - the task list API takes a single assignee
    const handleTeamOptionSelect = (option) => {
        if (option === 'all') {
            setOpenDropdown(null)
            setIsAllTasksView(true)
            return
        }
        handleTeamMemberChange(option === 'self' ? assignee : option)
    }

    const handleMentionsClick = () => {
        if (isMentionsView) return
        setIsMentionsView(true)
        setSelectedTaskId(null)
        setPendingScrollCommentId(null)
        setIsSelectingTask(false)
        clearTimeout(pinnedTaskRemovalTimeoutRef.current)
        clearTimeout(pinnedTaskLeaveTimeoutRef.current)
        setIsPinnedTaskLeaving(false)
        setPinnedTask(null)
    }

    const handleSortChange = (option) => {
        setSortBy(option)
        setOpenDropdown(null)
        resetTaskList({ sort: option })
    }

    const handlePriorityFilterChange = (option) => {
        setPriorityFilter(option)
        setOpenDropdown(null)
        resetTaskList({ priority: option })
    }

    const handleTaskSelect = (id, pinnedTaskData) => {
        setSelectedTaskId(id)
        setIsSelectingTask(true)
        dispatchHandler(dispatch, "getTaskDetailsRequest", { id })

        clearTimeout(pinnedTaskRemovalTimeoutRef.current)
        clearTimeout(pinnedTaskLeaveTimeoutRef.current)
        setIsPinnedTaskLeaving(false)
        if (pinnedTask && String(pinnedTask.id) !== String(id)) {
            const staleId = pinnedTask.id
            pinnedTaskRemovalTimeoutRef.current = setTimeout(() => {
                setIsPinnedTaskLeaving(true)
                pinnedTaskLeaveTimeoutRef.current = setTimeout(() => {
                    setPinnedTask((prev) => (prev && String(prev.id) === String(staleId) ? null : prev))
                    setIsPinnedTaskLeaving(false)
                }, 300)
            }, 1000)
        }
        if (pinnedTaskData && !isMentionsView && !taskListItems.some((item) => String(item.id) === String(id))) {
            setPinnedTask(pinnedTaskData)
        }
    }

    const handleMentionTaskClick = (taskId) => {
        setPendingScrollCommentId(latestMentionByTaskId[taskId]?.commentId ?? null)
        handleTaskSelect(taskId)
    }

    const handlePriorityChange = (value) => {
        setPriorityOverride((prev) => ({ ...prev, [selectedTaskId]: value }))
        dispatchHandler(dispatch, "changeTaskPriorityRequest", { id: selectedTaskId, priority: value })
    }

    const taskTypeRows = [...(taskTypeCountsData?.data?.resource || [])].sort((a, b) => b.count - a.count)
    const priorityOptions = taskPrioritiesData?.data?.resource || []
    const taskDetailsResource = taskDetailsData?.data?.resource
    // Only trust taskDetailsData once it actually belongs to the currently selected task - otherwise, right
    // after switching tasks (or leaving a shared task view), the previous task's cached data would flash/stick.
    const rawSelectedTask = (taskDetailsResource?.task && String(taskDetailsResource.task.id) === String(selectedTaskId))
        ? { ...taskDetailsResource.task, linkedData: taskDetailsResource.linkedData, childTasks: taskDetailsResource.childTasks }
        : null
    const selectedTask = rawSelectedTask
        ? { ...rawSelectedTask, priority: priorityOverride[selectedTaskId] || rawSelectedTask.priority }
        : null
    const activeTaskTypeLabel = getTaskTypeMeta(activeTaskType).label
    // Only count unread comments for documents actually in the "Comments" tab's own list - `unreadComments`
    // is an app-wide map (POs, ASNs, GRCs, etc.), so summing it whole would include documents that never
    // show up in this tab at all.
    const unreadCommentsTotal = (commentsDocumentsListData?.data?.data || []).reduce((sum, row) => {
        const key = `${row?.document_id}-${row?.document_no}`
        return sum + (Number(unreadComments?.[key]) || 0)
    }, 0)
    const tabCounts = { takeActions: taskTypeRows.reduce((sum, row) => sum + (row.count || 0), 0), comments: unreadCommentsTotal }
    const hasNoData = !taskTypeRows.length
    const teamTaskCountsResource = teamTaskCountsData?.data?.resource
    // Direct reports (depth 1) of the logged-in user - deeper levels are rolled up in the team dropdown
    const teamMemberRows = (Array.isArray(teamTaskCountsResource?.team?.members) ? teamTaskCountsResource.team.members : []).filter((member) => String(member.jti) !== String(assignee))
    const isOwnView = String(viewAssignee) === String(assignee)

    // Clear a pending scroll target shortly after it's handed to the detail panel, so it isn't
    // reused if the user later selects an unrelated (non-mention) task.
    useEffect(() => {
        if (!pendingScrollCommentId) return
        if (!selectedTask) return
        const timeoutId = setTimeout(() => setPendingScrollCommentId(null), 1500)
        return () => clearTimeout(timeoutId)
    }, [selectedTask?.id])

    // A manager with no tasks of their own would otherwise land on an empty "My Tasks" view - once both the
    // own counts (empty) and the team list are in, switch to the first teammate who has tasks so theirs show up
    useEffect(() => {
        if (isSharedTaskView || autoTeamSwitchDoneRef.current) return
        if (currentTab !== 'takeActions' || loadedListTabRef.current !== 'takeActions' || !isOwnView) return
        if (!taskTypeCountsData?.isSuccess || !teamTaskCountsData?.isSuccess || !hasNoData) return
        const firstMemberWithTasks = teamMemberRows.find((member) => member.taskCount)
        if (!firstMemberWithTasks) return
        autoTeamSwitchDoneRef.current = true
        handleTeamMemberChange(firstMemberWithTasks.jti)
    }, [taskTypeCountsData?.isSuccess, teamTaskCountsData?.isSuccess])

    if (isSharedTaskView) {
        return (
            <ParentModal
                getParentChildProps={{
                    closeModal: () => history.push('/task-manager'),
                    animationType: 'bottom',
                    parentModalOuterMostClassName: 'flex items-center justify-center',
                    postitionProps: { position: 'relative' },
                    task: selectedTask,
                    priorityOptions,
                    onPriorityChange: handlePriorityChange,
                }}
                childComponent={TaskPreviewModal}
            />
        )
    }

    return (
        <div className="flex flex-col h-[calc(100vh-45px)] overflow-hidden bg-white pl-5">
            <div className="flex items-center justify-between px-4 pt-1 pb-[5px] border-b border-borderSlateGrey bg-white">
                <div className="flex items-center gap-5">
                    {Object.keys(TABS).map((key) => (
                        <React.Fragment key={key}>
                            {key === 'archieve' && <span className="h-4 w-px bg-borderSlateGrey" />}
                            <button
                                type="button"
                                onClick={() => handleTabChange(key)}
                                className={`flex items-center gap-2 text-[13px] font-medium pb-3 -mb-3 border-b-2 ${currentTab === key ? 'text-primary border-primary' : 'text-fontSlateGrey border-transparent hover:text-fontBlack'}`}
                            >
                                <LanguageTranslator>{TABS[key]}</LanguageTranslator>
                                {key !== 'archieve' && (
                                    <span className={`text-[10px] rounded-[5px] px-2 py-0.5 ${currentTab === key ? 'bg-primary text-white' : 'bg-[#F1F4F9] text-fontSlateGrey'}`}>{tabCounts[key]}</span>
                                )}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => setIsCreateTaskOpen(true)}
                    className="flex items-center gap-1.5 bg-primary text-white text-[12px] font-medium px-4 py-2 rounded-[4px] hover:opacity-90 shrink-0"
                >
                    <Plus size={15} />
                    <LanguageTranslator>Create Task</LanguageTranslator>
                </button>
            </div>

            {currentTab === 'comments' ? (
                <div className="flex flex-1 min-h-0 gap-2 p-4 bg-white">
                    <Comments fillParent variant="taskManager" />
                </div>
            ) : (isTabSwitching || (hasNoData && teamTaskCountsData?.isLoading)) ? (
                <div className="flex flex-1 min-h-0 items-center justify-center bg-white">
                    <GenericSpinnerLoader loaderSize={{ width: '32', height: '32' }} />
                </div>
            ) : (
            <div className="flex flex-1 min-h-0 gap-2 p-4 bg-white">
                <div className="w-[240px] rounded-[8px] border border-borderSlateGrey bg-white py-3 shrink-0 flex flex-col min-h-0">
                    {currentTab === 'archieve' ? (
                        <p className="px-4 mb-2 text-xs font-semibold text-fontSlateGrey tracking-wide shrink-0">TASK TYPES</p>
                    ) : (
                    <div ref={teamDropdownRef} className="relative px-4 mb-2 shrink-0">
                        <TeamTasksDropdown
                            isOpen={openDropdown === 'team'}
                            onToggle={() => setOpenDropdown(openDropdown === 'team' ? null : 'team')}
                            data={teamTaskCountsResource}
                            selectedOption={isAllTasksView ? 'all' : (isOwnView ? 'self' : viewAssignee)}
                            onSelect={handleTeamOptionSelect}
                        />
                    </div>
                    )}
                    <div className={`flex-1 min-h-0 ${taskTypeRows.length ? 'overflow-y-auto' : 'overflow-hidden'}`}>
                        {taskTypeRows.length ? taskTypeRows.map((row) => {
                            const meta = getTaskTypeMeta(row.task_type)
                            const Icon = meta.icon
                            const isActive = !isMentionsView && activeTaskType === row.task_type
                            return (
                                <button
                                    key={row.task_type}
                                    type="button"
                                    onClick={() => handleTaskTypeClick(row.task_type)}
                                    className={`flex items-center justify-between w-full px-4 py-2.5 text-[13px] border-l-[3px] ${isActive ? 'bg-primary_w_less_opacity border-l-primary text-primary font-medium' : 'border-l-transparent text-fontBlack hover:bg-[#F8FAFC]'}`}
                                >
                                    <span className="flex items-center min-w-0 gap-2">
                                        <Icon size={15} className="shrink-0" />
                                        <TextWithTooltip as="span" width="100%" text={LanguageTranslator({ children: meta.label })} />
                                    </span>
                                    <span className={`text-xs rounded-[5px] min-w-[20px] px-1.5 py-0.5 text-center shrink-0 ${isActive ? 'bg-primary text-white' : 'bg-[#F1F4F9] text-fontSlateGrey'}`}>{row.count}</span>
                                </button>
                            )
                        }) : (
                            <NoDataFound primaryText="No Task Types Found" />
                        )}
                    </div>
                    {currentTab !== 'archieve' && isOwnView && (
                    <div className="shrink-0 pt-2 sticky bottom-0 bg-white">
                        <span className="block h-px mb-2 bg-borderSlateGrey" />
                        <button
                            type="button"
                            onClick={handleMentionsClick}
                            className={`flex items-center justify-between w-full px-4 py-2.5 text-[13px] border-l-[3px] ${isMentionsView ? 'bg-primary_w_less_opacity border-l-primary text-primary font-medium' : 'border-l-transparent text-fontBlack hover:bg-[#F8FAFC]'}`}
                        >
                            <span className="flex items-center min-w-0 gap-2">
                                <span className="flex items-center justify-center h-[20px] w-[20px] rounded-full bg-primary text-white shrink-0 aspect-square">
                                    <AtSign size={14} strokeWidth={2.25} />
                                </span>
                                <TextWithTooltip as="span" width="100%" text={LanguageTranslator({ children: 'Mentions' })} />
                            </span>
                            <span className="text-xs rounded-[5px] min-w-[20px] px-1.5 py-0.5 text-center shrink-0 bg-successColor text-white">{taskCountWithUnreadMentions}</span>
                        </button>
                    </div>
                    )}
                </div>

                <div className="w-[401px] rounded-[8px] border border-borderSlateGrey bg-white flex flex-col min-h-0 shrink-0 relative">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-borderSlateGrey">
                        <p className="mb-0 text-xs font-semibold text-fontSlateGrey tracking-wide">{isMentionsView ? 'MENTIONS' : activeTaskTypeLabel.toUpperCase()}</p>
                        {!isMentionsView && (
                        <div ref={listDropdownRef} className="flex items-center gap-2">
                            <div className="relative">
                                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')} className="flex items-center gap-1 text-xs text-fontSlateGrey hover:text-fontBlack">
                                    <LanguageTranslator>Priority</LanguageTranslator> <ChevronDown size={12} />
                                </button>
                                {openDropdown === 'priority' && (
                                    <div className="absolute top-[calc(100%+4px)] right-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[3px] py-1 w-[100px]">
                                        {['All', ...priorityOptions].map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => handlePriorityFilterChange(option)}
                                                className={`w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs hover:bg-primary_w_less_opacity ${priorityFilter === option ? 'text-primary font-medium' : 'text-fontBlack'}`}
                                            >
                                                {option}
                                                {priorityFilter === option && <Check size={12} />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="relative">
                                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')} className="flex items-center gap-1 text-xs text-fontSlateGrey hover:text-fontBlack">
                                    {sortBy} <ChevronDown size={12} />
                                </button>
                                {openDropdown === 'sort' && (
                                    <div className="absolute top-[calc(100%+4px)] right-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[3px] py-1 w-[100px]">
                                        {SORT_OPTIONS.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => handleSortChange(option)}
                                                className={`w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs hover:bg-primary_w_less_opacity ${sortBy === option ? 'text-primary font-medium' : 'text-fontBlack'}`}
                                            >
                                                {option}
                                                {sortBy === option && <Check size={12} />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {!isMentionsView && pinnedTask && !taskListItems.some((item) => String(item.id) === String(pinnedTask.id)) && (
                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isPinnedTaskLeaving ? 'opacity-0 max-h-0' : 'opacity-100 max-h-[200px]'}`}>
                                <TaskManagerListItem
                                    key={`pinned-${pinnedTask.id}`}
                                    item={pinnedTask}
                                    isActive={selectedTaskId === pinnedTask.id}
                                    onClick={() => handleTaskSelect(pinnedTask.id)}
                                />
                            </div>
                        )}
                        {(isMentionsView ? mentionDocumentIds.map((id) => mentionsTaskCache[id]).filter(Boolean) : taskListItems).map((task) => (
                            <TaskManagerListItem
                                key={task.id}
                                item={{
                                    ...task,
                                    ...(priorityOverride[task.id] ? { priority: priorityOverride[task.id] } : {}),
                                    ...(isMentionsView ? {
                                        mentionedByName: latestMentionByTaskId[task.id]?.taggedByUserName || 'Someone',
                                        mentionUnread: !latestMentionByTaskId[task.id]?.isRead,
                                    } : {}),
                                }}
                                isActive={selectedTaskId === task.id}
                                onClick={() => (isMentionsView ? handleMentionTaskClick(task.id) : handleTaskSelect(task.id))}
                            />
                        ))}
                        {isMentionsView
                            ? !mentionDocumentIds.length && <NoDataFound primaryText="No Mentions Found" />
                            : !taskListItems.length && <NoDataFound primaryText="No Tasks Found" />}
                        {!isMentionsView && !!taskListItems.length && hasMoreTasks && <div ref={listSentinelRef} className="h-1" />}
                    </div>
                </div>

                <div className="flex-1 min-h-0 rounded-[8px] border border-borderSlateGrey bg-[#F7FAF8] overflow-hidden relative">
                    <TaskManagerDetailPanel task={selectedTask} onPriorityChange={handlePriorityChange} priorityOptions={priorityOptions} scrollToCommentId={pendingScrollCommentId} onChildTaskSelect={handleTaskSelect} />
                </div>
            </div>
            )}

            {isCreateTaskOpen && (
                <ParentModal
                    getParentChildProps={{
                        closeModal: () => setIsCreateTaskOpen(false),
                        dimensions: { width: '760px', height: '85vh' },
                        postitionProps: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '12px', overflow: 'hidden' },
                    }}
                    childComponent={CreateTaskModal}
                />
            )}
        </div>
    )
}

export default TaskManagerPage
